import { OrganizationDTO, OrganizationGroupDTO, OrganizationGroupsApi, OrganizationsApi } from '@reposit/api-client';
import { AxiosResponse } from 'axios';
import { call, put, takeLatest } from 'redux-saga/effects';
import { FlashState } from '../../components/FlashMessage';
import { getErrorMessage } from '../../utils/common.utils';
import { syncEntitiesAndGetResults } from '../entities/entities.sagas';
import { FlashMessagesActionTypes, setFlashMessage } from '../flash-messages/flash-messages.actions';
import SCHEMA from '../schema';
import { createOrganizationGroupsApi, createOrganizationsApi, runSagaWithAuth } from '../utils/api.utils';
import {
  CREATE_ORG_GROUP_API_REQUESTED,
  CREATE_ORG_GROUP_STORE_KEY,
  createOrganizationGroupFailed,
  createOrganizationGroupSuccess,
  OrganizationGroupActionTypes,
  UPDATE_ORG_GROUP_API_REQUESTED,
  UPDATE_ORG_GROUP_STORE_KEY,
  updateOrganizationGroupFailed,
  updateOrganizationGroupSuccess,
} from './organization-group.actions';
import { CreateOrganizationGroupPayload, UpdateOrganizationGroupPayload } from './organization-group.types';

export function* updateOrganizationGroup({ payload }: { payload: UpdateOrganizationGroupPayload; type: string }) {
  try {
    const { organizationId, ...changes } = payload;
    const organizationsApi: OrganizationsApi = yield createOrganizationsApi();
    const response: AxiosResponse<OrganizationDTO> = yield call(
      runSagaWithAuth(() => organizationsApi.updateOrganizationParentGroups(organizationId, changes)),
    );

    yield syncEntitiesAndGetResults(response.data, SCHEMA.organization);
    yield put<OrganizationGroupActionTypes>(
      updateOrganizationGroupSuccess({
        id: response.data.id,
        organizationGroup: response.data.organizationGroup
          ? { id: response.data.organizationGroup.id, name: response.data.organizationGroup.name }
          : null,
        parentOrganizationGroup: response.data.parentOrganizationGroup
          ? { id: response.data.parentOrganizationGroup.id, name: response.data.parentOrganizationGroup.name }
          : null,
      }),
    );
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({
        key: UPDATE_ORG_GROUP_STORE_KEY,
        message: 'Success! Parent/Ultimate Parent updated!',
        state: FlashState.SUCCESS,
      }),
    );
  } catch (e) {
    const error = getErrorMessage(e);
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({ key: UPDATE_ORG_GROUP_STORE_KEY, message: error, state: FlashState.ERROR }),
    );
    yield put<OrganizationGroupActionTypes>(updateOrganizationGroupFailed(error));
  }
}

export function* createOrganizationGroup({ payload }: { payload: CreateOrganizationGroupPayload; type: string }) {
  try {
    const organizationGroupsApi: OrganizationGroupsApi = yield createOrganizationGroupsApi();
    const response: AxiosResponse<OrganizationGroupDTO> = yield call(
      runSagaWithAuth(() => organizationGroupsApi.createOrganizationGroup({ name: payload.name })),
    );

    yield put<OrganizationGroupActionTypes>(createOrganizationGroupSuccess(response.data, payload.requestId));
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({
        key: CREATE_ORG_GROUP_STORE_KEY,
        message: 'Success! Parent/Ultimate Parent created!',
        state: FlashState.SUCCESS,
      }),
    );
  } catch (e) {
    const error = getErrorMessage(e);
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({ key: CREATE_ORG_GROUP_STORE_KEY, message: error, state: FlashState.ERROR }),
    );
    yield put<OrganizationGroupActionTypes>(createOrganizationGroupFailed(error));
  }
}

export function* watchOrganizationGroupSagas() {
  yield takeLatest(UPDATE_ORG_GROUP_API_REQUESTED, updateOrganizationGroup);
  yield takeLatest(CREATE_ORG_GROUP_API_REQUESTED, createOrganizationGroup);
}
